<template>
	<UBanner
		:id="id"
		:to="to"
		:target="target"
		:color="color"
		:icon="icon"
		:title="title"
		:close="closeable"
		:hide-banner="false"
		:ui="{
			center: [showExpanded ? 'items-start py-3.5 flex-col sm:items-center sm:py-0 sm:flex-row' : 'flex-row'],
			actions: [showExpanded ? 'self-center' : ''],
			right: [showExpanded ? 'absolute top-2 right-4 sm:relative sm:top-0 sm:right-0' : ''],
			title: titleClasses
		}"
		:actions="allActions"
		@close="handleLocalstorage"
	>
		<template #title>
			<span>
				<span class="font-semibold">
					{{ title }}
				</span>

				<span v-if="updated">
					(updated
					<NuxtTime
						:datetime="updated"
						relative
					/>) - {{ message }}
				</span>
			</span>
		</template>
	</UBanner>
</template>

<script setup lang="ts">
import type { ButtonProps } from '@nuxt/ui'

const props = defineProps({
	id: {
		type: String,
		required: true
	},
	color: {
		type: String,
		default: 'warning'
	},
	title: {
		type: String,
		default: 'Alert Banner Test'
	},
	message: {
		type: String,
		default: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.'
	},
	updated: {
		type: String,
		default: ''
	},
	active: {
		type: Boolean,
		default: true
	},
	expandable: {
		type: Boolean,
		default: true
	},
	expanded: {
		type: Boolean,
		default: false
	},
	closeable: {
		type: Boolean,
		default: true
	},
	alwaysShowOnLoad: {
		type: Boolean,
		default: false
	},
	icon: {
		type: String,
		default: 'i-lucide-info'
	},
	closeIcon: {
		type: String,
		default: 'i-lucide-x'
	},
	to: {
		type: String,
		default: ''
	},
	target: {
		type: String,
		default: '_self'
	},
	actions: {
		type: Array as PropType<ButtonProps[]>,
		default: () => []
	}
})

const {
	id,
	title,
	message,
	updated,
	active,
	expandable,
	expanded,
	actions,
	closeable,
	alwaysShowOnLoad
} = toRefs(props)

const showExpanded = ref(expanded.value)

watch(expanded, newValue => {
	showExpanded.value = newValue
})

const toggleExpanded = () => {
	showExpanded.value = !showExpanded.value
}

const actionLabel = computed(() => {
	return showExpanded.value ? 'Collapse' : 'Expand'
})

const allActions = computed(() => {
	// if expandable is true, add the toggle action to the existing actions
	if (!expandable.value) {
		return actions.value
	}
	// Otherwise, return the actions with the toggle action included
	return [
		...actions.value,
		{
			label: actionLabel.value,
			variant: 'outline',
			trailingIcon: showExpanded.value ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down',
			onClick: toggleExpanded
		}
	]
})

const titleClasses = computed(() => {
	// Override the default truncate class when expanded
	if (showExpanded.value) {
		return 'text-sm text-inverted font-medium whitespace-normal'
	}
	// Keep the default behavior when collapsed
	return 'text-sm text-inverted font-medium line-clamp-2'
})

// remove the banner from local storage when closed so it can be shown again
const handleLocalstorage = () => {
	if (import.meta.client && closeable.value && alwaysShowOnLoad.value && active.value) {
		localStorage.removeItem(`banner-${id.value}`)
	}
}

// scroll to the top of the page on mounted
onMounted(() => {
	if (import.meta.client && !localStorage.getItem(`banner-${id.value}`)) {
		window.scrollTo(0, 0)
	}
})
</script>
